import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Check, Eye, Edit2, XCircle, FileSpreadsheet, X, Trash2, BarChart3 } from "lucide-react";
import useServerTable from "../hooks/useServerTable";
import { useMutation } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import stitchingEntryApi from "../api/stitchingEntryApi";
import { downloadBlob, blobErrorMessage } from "../utils/downloadBlob";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Input from "../components/Input";
import Modal from "../components/Modal";
import Alert from "../components/Alert";
import Badge from "../components/Badge";
import Table, { ActionBtn } from "../components/Table";
import Select2 from "../components/Select2";
import SearchBar from "../components/SearchBar";
import WorkLocationSelect from "../components/WorkLocationSelect";
import useMyLocations from "../hooks/useMyLocations";
import T from "../theme/colors";

const fmtAmt = n => "₹" + (Number(n) || 0).toFixed(2);
const fmtDate = d => d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN",
  { day: "2-digit", month: "short", year: "numeric" }) : "—";
// Local date, not toISOString() (UTC): between midnight and 5:30 AM IST the UTC
// date is still yesterday.
const localToday = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};
const empLabel = e => `${e.employeeName}${e.employeeCode ? ` (${e.employeeCode})` : ""}`
  + `${e.departmentName ? ` · ${e.departmentName}` : ""}`;

/**
 * Stitching Entry — issue and inward in ONE entry, a day's sheet.
 *
 * Pick the date, then add a row per piece of work: employee, barcode,
 * operation, qty - many employees on one sheet. Only the operations linked to
 * the barcode in Barcode Operations are offered, at the job work rate per
 * piece set there; the rate is fixed on the line when saved.
 *
 * Saved lines show up in Job Work Entry for their employee — pick them on a
 * new entry, or use "Generate from Stitching" there for every employee at once.
 * A billed line is fixed; cancel the job work entry to change it. The
 * Stitching Report lists every line by date, employee, barcode and operation.
 */
export default function StitchingEntryPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("STITCHING_ENTRY", "CREATE");
  const canEdit = hasPermission("STITCHING_ENTRY", "EDIT");
  // Cancelling a confirmed entry is a status change: Edit.
  const canCancel = hasPermission("STITCHING_ENTRY", "EDIT");
  const canExport = hasPermission("STITCHING_ENTRY", "EXPORT");
  const { mutate } = useMutation();

  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    stitchingEntryApi.employees()
      .then(res => setEmployees(res.data?.data || res.data || []))
      .catch(() => setEmployees([]));
  }, []);

  const { locations } = useMyLocations();
  const [search, setSearch] = useState("");
  const [fLoc, setFLoc] = useState("");
  const [fEmp, setFEmp] = useState("");
  const [fStatus, setFStatus] = useState("CONFIRMED");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const filters = {
    q: search.trim() || undefined,
    locationId: fLoc || undefined,
    employeeId: fEmp || undefined,
    status: fStatus || undefined,
    from: fFrom || undefined,
    to: fTo || undefined,
  };

  const t = useServerTable(stitchingEntryApi.paged, {
    defaultSortBy: "entryDate",
    defaultSortDir: "desc",
    defaultSize: 25,
    debounceMs: 300,
    extraFilters: filters,
  });

  const [form, setForm] = useState(null);   // { entryId? }
  const [viewId, setViewId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState("");

  const exportExcel = async () => {
    setExporting(true); setExportErr("");
    try {
      downloadBlob(await stitchingEntryApi.exportExcel(filters), "stitching-entries.xlsx");
    } catch (e) {
      setExportErr(await blobErrorMessage(e, "Excel export failed"));
    }
    setExporting(false);
  };

  const cancelEntry = row => {
    if (!window.confirm(`Cancel ${row.entryNumber}?\n\nIts ${row.totalQty} pcs will no longer be billable in Job Work.`)) return;
    mutate(() => stitchingEntryApi.cancel(row.id), { onSuccess: t.reload, successMsg: `${row.entryNumber} cancelled` });
  };

  const empOptions = [{ value: "", label: "All employees" },
    ...employees.map(e => ({ value: String(e.id), label: empLabel(e) }))];

  const cols = [
    {
      key: "entryNumber", label: "Entry No", sortKey: "entryNumber",
      render: v => <span style={{ fontFamily: "monospace", fontWeight: 700, color: "var(--primary)" }}>{v}</span>,
    },
    { key: "entryDate", label: "Date", sortKey: "entryDate", render: v => fmtDate(v) },
    ...(locations.length > 1 ? [{
      key: "locationName", label: "Location",
      render: v => <span style={{ color: "var(--text-1)", fontWeight: 600 }}>{v || "—"}</span>,
    }] : []),
    {
      key: "employeeNames", label: "Employees",
      render: (v, row) => {
        const names = v && v.length ? v : (row.employeeName ? [row.employeeName] : []);
        return (
          <div title={names.join(", ")}>
            <div style={{ fontWeight: 600, color: "var(--text-0)" }}>
              {names.length <= 1 ? (names[0] || "—") : `${names.length} employees`}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-2)" }}>
              {names.length > 1
                ? names.slice(0, 3).join(", ") + (names.length > 3 ? ` +${names.length - 3}` : "")
                : [row.employeeCode, row.departmentName].filter(Boolean).join(" · ")}
            </div>
          </div>
        );
      },
    },
    {
      key: "barcodeCount", label: "Work",
      render: (v, row) => (
        <span style={{ color: "var(--text-1)" }}>
          {v} barcode{v === 1 ? "" : "s"} · {row.operationCount} op{row.operationCount === 1 ? "" : "s"}
        </span>
      ),
    },
    { key: "totalQty", label: "Qty", sortKey: "totalQty", render: v => <b style={{ color: "var(--text-0)" }}>{v}</b> },
    {
      key: "totalAmount", label: "Amount", sortKey: "totalAmount",
      render: v => <b style={{ color: "var(--text-0)" }}>{fmtAmt(v)}</b>,
    },
    {
      key: "jobWorkEntryNumbers", label: "Job Work",
      render: (v, row) => v && v.length
        ? (
          <span>
            <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: "var(--success)" }}>{v.join(", ")}</span>
            {row.pendingLineCount > 0 && (
              <span style={{ fontSize: 11, color: "var(--warn)", marginLeft: 6 }}>+{row.pendingLineCount} pending</span>
            )}
          </span>
        )
        : row.status === "CONFIRMED"
          ? <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
            background: "var(--warn-bg)", color: "var(--warn)", border: "1px solid var(--warn-bdr)",
          }}>PENDING</span>
          : <span style={{ color: "var(--text-2)" }}>—</span>,
    },
    { key: "status", label: "Status", render: v => <Badge label={v} /> },
  ];

  // A billed line is fixed, the rest of the sheet can still change; the whole
  // sheet is fixed only once every line is billed. Cancelling needs none billed.
  const editable = row => row.status === "CONFIRMED" && row.pendingLineCount > 0;
  const cancellable = row => row.status === "CONFIRMED" && !(row.jobWorkEntryNumbers || []).length;

  return (
    <div className="fade-up" style={{ padding: "20px 24px" }}>
      <PageHeader
        title="Stitching Entry"
        subtitle="A day's sheet: pick the date, then a row per piece of work — employee, barcode, operation, qty. Billed employee-wise in Job Work Entry."
        breadcrumb={["Production", "Stitching Entry"]}
        actions={<>
          <Btn variant="outline" size="sm" icon={<BarChart3 size={13} />}
            onClick={() => { window.location.hash = "stitching-report"; }}>Report</Btn>
          {canExport && <Btn variant="outline" size="sm" icon={<FileSpreadsheet size={13} />}
            loading={exporting} onClick={exportExcel}>Excel</Btn>}
          {canCreate && <Btn size="sm" icon={<Plus size={13} />} onClick={() => setForm({})}>New Entry</Btn>}
        </>}
      />

      <Alert message={exportErr} onClose={() => setExportErr("")} />

      <Card>
        <div style={{
          padding: "12px 16px", borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
        }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search entry, employee, barcode, operation…" />
          <WorkLocationSelect mode="filter" value={fLoc} onChange={setFLoc} />
          <div style={{ minWidth: 220 }}>
            <Select2 value={fEmp} onChange={setFEmp} placeholder="All employees" options={empOptions} />
          </div>
          <div style={{ minWidth: 140 }}>
            <Select2 value={fStatus} onChange={setFStatus} placeholder="All statuses"
              options={[{ value: "", label: "All statuses" },
              { value: "CONFIRMED", label: "Confirmed" },
              { value: "CANCELLED", label: "Cancelled" }]} />
          </div>
          <input type="date" value={fFrom} onChange={e => setFFrom(e.target.value)} title="From" style={dateInput} />
          <span style={{ color: "var(--text-2)", fontSize: 12 }}>to</span>
          <input type="date" value={fTo} onChange={e => setFTo(e.target.value)} title="To" style={dateInput} />
        </div>

        <Table
          columns={cols}
          serverMode t={t}
          emptyText={search || fLoc || fEmp || fFrom || fTo ? "No entries match" : "No stitching entries yet — click New Entry"}
          extraActions={row => (<>
            <ActionBtn icon={<Eye size={13} />} color="var(--info)" tooltip="View" onClick={() => setViewId(row.id)} />
            {canEdit && editable(row) && (
              <ActionBtn icon={<Edit2 size={13} />} color="var(--info)" tooltip="Edit"
                onClick={() => setForm({ entryId: row.id })} />
            )}
            {canCancel && cancellable(row) && (
              <ActionBtn icon={<XCircle size={13} />} color="var(--danger)" tooltip="Cancel"
                onClick={() => cancelEntry(row)} />
            )}
          </>)}
        />
      </Card>

      {form && (
        <EntryModal entryId={form.entryId}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); t.reload(); }} />
      )}
      {viewId && <ViewModal entryId={viewId} onClose={() => setViewId(null)} />}
    </div>
  );
}

/**
 * Barcode picker for one grid row: type (or scan) part of a barcode, product
 * or SO number; matching sale order lines come from the server. The row's
 * current barcode stays an option so it keeps showing.
 */
function BarcodePicker({ value, onPick, disabled, locationId }) {
  const [term, setTerm] = useState("");
  const [opts, setOpts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setOpts([]); return; }
    let alive = true;
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await stitchingEntryApi.soItems(q, locationId);
        const rows = res.data?.data || res.data || [];
        if (alive) setOpts(Array.isArray(rows) ? rows : []);
      } catch {
        if (alive) setOpts([]);
      }
      if (alive) setBusy(false);
    }, 250);
    return () => { alive = false; clearTimeout(h); };
  }, [term, locationId]);

  const all = value && !opts.some(o => o.saleOrderItemId === value.saleOrderItemId) ? [value, ...opts] : opts;
  const options = all.map(o => ({
    value: String(o.saleOrderItemId),
    label: `${o.barcode} — ${o.productName || "?"}${o.soNumber ? ` · ${o.soNumber}` : ""}`,
  }));

  return (
    <Select2
      value={value ? String(value.saleOrderItemId) : ""}
      onChange={v => { const o = all.find(x => String(x.saleOrderItemId) === String(v)); if (o) onPick(o); }}
      onSearch={setTerm}
      options={options}
      disabled={disabled}
      placeholder={busy ? "Searching…" : "Type or scan barcode…"}
    />
  );
}

let rowSeq = 0;
const newRow = (from = {}) => ({
  key: ++rowSeq,
  id: null,                         // saved line id (edit)
  employeeId: from.employeeId || "",
  soItem: from.soItem || null,      // { saleOrderItemId, barcode, productName, soNumber, ... }
  operationId: "",
  qty: "",
  billedBy: null,                   // job work entry number when billed (fixed)
  savedOp: null,                    // { operationId, operationName, jobWorkRate } no longer linked
});

/**
 * Create / edit a day's sheet. Pick the DATE first, then one row per piece of
 * work - Employee, Barcode, Operation, Qty - with "Add row" for the next. The
 * rate per piece comes from the barcode's operation (Barcode Operations);
 * a new row starts with the previous row's employee and barcode.
 *
 * Rows billed in a live Job Work Entry are fixed (shown greyed); the rest of
 * the sheet stays editable.
 */
function EntryModal({ entryId, onClose, onSaved }) {
  const isEdit = !!entryId;
  const { mutate, loading: saving } = useMutation();
  // The sheet's location: its employees, the order lines it has a share of and
  // what is left of that share all depend on it.
  const [locationId, setLocationId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [entryNumber, setEntryNumber] = useState("");
  const [entryDate, setEntryDate] = useState(localToday());
  const [notes, setNotes] = useState("");
  const [allowExtra, setAllowExtra] = useState(false);
  const [rows, setRows] = useState(() => (isEdit ? [] : [newRow()]));
  // Operations per sale order line: { [soItemId]: { configured, operations } }
  const [opsBySo, setOpsBySo] = useState({});
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(isEdit);

  // Employees who work at the sheet's location (and those with no location).
  useEffect(() => {
    if (!locationId) return;
    let alive = true;
    stitchingEntryApi.employees(locationId)
      .then(res => { if (alive) setEmployees(res.data?.data || res.data || []); })
      .catch(() => { if (alive) setEmployees([]); });
    return () => { alive = false; };
  }, [locationId]);

  // A new sheet moved to another location starts over: its rows were picked
  // from the old location's people and order lines.
  const prevLoc = useRef("");
  useEffect(() => {
    if (!isEdit && prevLoc.current && prevLoc.current !== locationId) {
      setRows([newRow()]);
      setOpsBySo({});
    }
    prevLoc.current = locationId;
  }, [locationId, isEdit]);

  const loadOps = async soItemId => {
    if (!soItemId || opsBySo[soItemId]) return;
    try {
      const r = await stitchingEntryApi.soItemOperations(soItemId, entryId, locationId);
      const d = r.data?.data ?? r.data;
      setOpsBySo(p => ({ ...p, [soItemId]: { configured: d.barcodeConfigured, operations: d.operations || [] } }));
    } catch (ex) {
      setErr(ex?.response?.data?.message || "Could not load that barcode's operations");
    }
  };

  // Edit: one row per saved line.
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      try {
        const res = await stitchingEntryApi.getById(entryId);
        const e = res.data?.data ?? res.data;
        if (!alive) return;
        setEntryNumber(e.entryNumber);
        setLocationId(e.locationId ? String(e.locationId) : "");
        setEntryDate(e.entryDate);
        setNotes(e.notes || "");
        const built = (e.items || []).map(it => ({
          ...newRow(),
          id: it.id,
          employeeId: String(it.employeeId ?? e.employeeId ?? ""),
          soItem: {
            saleOrderItemId: it.saleOrderItemId, barcode: it.barcode, productName: it.productName,
            soNumber: it.soNumber, productBarcode: it.productBarcode,
          },
          operationId: String(it.operationId),
          qty: String(it.qty),
          billedBy: it.jobWorkEntryNumber || null,
          savedOp: { operationId: it.operationId, operationName: it.operationName, jobWorkRate: it.jobWorkRate },
        }));
        setRows(built.length ? built : [newRow()]);
        const ids = [...new Set(built.map(r => r.soItem.saleOrderItemId))];
        const loaded = {};
        for (const id of ids) {
          try {
            const r = await stitchingEntryApi.soItemOperations(id, entryId, e.locationId);
            const d = r.data?.data ?? r.data;
            loaded[id] = { configured: d.barcodeConfigured, operations: d.operations || [] };
          } catch { /* the row keeps its saved operation */ }
        }
        if (alive) setOpsBySo(loaded);
      } catch (ex) {
        if (alive) setErr(ex?.response?.data?.message || "Could not load the entry");
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
    // loadOps is not needed here - the edit path loads every barcode at once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, isEdit]);

  const patchRow = (key, p) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...p } : r)));
  const addRow = () => setRows(rs => [...rs, newRow(rs[rs.length - 1] || {})]);
  const removeRow = key => setRows(rs => (rs.length > 1 ? rs.filter(r => r.key !== key) : [newRow()]));

  // The operations a row can pick: the barcode's linked ones, plus the one it
  // was saved with if that is no longer linked (kept at its saved rate).
  const opsFor = r => {
    const list = [...((r.soItem && opsBySo[r.soItem.saleOrderItemId]?.operations) || [])];
    if (r.savedOp && !list.some(o => String(o.operationId) === String(r.savedOp.operationId))) {
      list.push({ ...r.savedOp, remainingQty: null, saved: true });
    }
    return list;
  };
  const opOf = r => opsFor(r).find(o => String(o.operationId) === String(r.operationId));

  // Pieces of each (barcode, operation) typed on THIS sheet - "left" shown per
  // row is what the order still allows after the other rows.
  const sheetQty = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (!r.soItem || !r.operationId) return;
      const k = `${r.soItem.saleOrderItemId}|${r.operationId}`;
      m[k] = (m[k] || 0) + (Number(r.qty) || 0);
    });
    return m;
  }, [rows]);

  const dupKeys = useMemo(() => {
    const seen = new Map();
    rows.forEach(r => {
      if (!r.employeeId || !r.soItem || !r.operationId) return;
      const k = `${r.employeeId}|${r.soItem.saleOrderItemId}|${r.operationId}`;
      seen.set(k, (seen.get(k) || 0) + 1);
    });
    return new Set([...seen].filter(([, n]) => n > 1).map(([k]) => k));
  }, [rows]);

  const totals = useMemo(() => {
    let qty = 0, amt = 0;
    rows.forEach(r => {
      const q = Number(r.qty) || 0;
      qty += q; amt += q * (Number(opOf(r)?.jobWorkRate) || 0);
    });
    return { qty, amt };
    // opOf reads opsBySo, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, opsBySo]);

  const save = async () => {
    if (!entryDate) { setErr("Pick the date first"); return; }
    const items = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const blank = !r.employeeId && !r.soItem && !r.operationId && !r.qty;
      if (blank) continue;
      const n = i + 1;
      if (!r.employeeId) { setErr(`Row ${n}: pick the employee`); return; }
      if (!r.soItem) { setErr(`Row ${n}: pick the barcode`); return; }
      if (!r.operationId) { setErr(`Row ${n}: pick the operation`); return; }
      const q = Number(r.qty);
      if (!Number.isInteger(q) || q <= 0) { setErr(`Row ${n}: qty must be a whole number above 0`); return; }
      if (dupKeys.has(`${r.employeeId}|${r.soItem.saleOrderItemId}|${r.operationId}`)) {
        setErr(`Row ${n}: the same employee, barcode and operation is on two rows - put the qty on one`); return;
      }
      items.push({
        employeeId: Number(r.employeeId), saleOrderItemId: r.soItem.saleOrderItemId,
        operationId: Number(r.operationId), qty: q,
      });
    }
    if (!items.length) { setErr("Add at least one row"); return; }
    const body = {
      locationId: locationId ? Number(locationId) : undefined,
      entryDate, notes: notes.trim() || null, allowExtra, items,
    };
    await mutate(
      () => isEdit ? stitchingEntryApi.update(entryId, body) : stitchingEntryApi.create(body),
      {
        onSuccess: saved => onSaved(saved),
        successMsg: isEdit ? "Entry updated" : "Stitching entry saved",
        onError: m => setErr(m),
      });
  };

  const empOptions = employees.map(e => ({ value: String(e.id), label: empLabel(e) }));
  const people = new Set(rows.filter(r => r.employeeId).map(r => r.employeeId)).size;

  return (
    <Modal open onClose={onClose} width={1180}
      title={isEdit ? `Edit ${entryNumber || "Entry"}` : "New Stitching Entry"}
      subtitle="Pick the date, then add a row for each piece of work: employee, barcode, operation, qty"
      footer={<>
        <span style={{ marginRight: "auto", fontSize: 14, color: "var(--text-1)" }}>
          {rows.length} row{rows.length === 1 ? "" : "s"} · {people} employee{people === 1 ? "" : "s"} ·
          Total <b style={{ color: "var(--text-0)" }}>{totals.qty} pcs</b> ·
          <b style={{ color: "var(--success)" }}> {fmtAmt(totals.amt)}</b>
        </span>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} loading={saving} disabled={loading} icon={<Check size={13} />}>
          {isEdit ? "Update Entry" : "Save Entry"}
        </Btn>
      </>}>
      <Alert message={err} onClose={() => setErr("")} />

      <div style={{ display: "grid", gridTemplateColumns: "220px 180px 1fr", gap: 12, marginBottom: 10 }}>
        {/* An existing sheet stays where it was made. */}
        <WorkLocationSelect mode="entry" label="Location" value={locationId} onChange={setLocationId}
          disabled={isEdit} />
        <Input label="Date" required type="date" value={entryDate} onChange={setEntryDate} />
        <Input label="Notes" value={notes} onChange={setNotes} placeholder="Optional" />
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-1)", margin: "0 0 10px" }}>
        <input type="checkbox" checked={allowExtra} onChange={e => setAllowExtra(e.target.checked)} />
        Allow extra qty (more pieces than the sale order line — extra cut or rework)
      </label>

      {loading ? (
        <div style={{ padding: 20, textAlign: "center", color: "var(--text-2)" }}>Loading…</div>
      ) : (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "visible" }}>
          {/* Fixed layout: long names ellipsize inside their pickers instead of
              pushing Amount out of the dialog. */}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
            <thead>
              <tr style={{ background: "var(--bg-input)" }}>
                <th style={{ ...th, width: 34 }}>#</th>
                <th style={th}>Employee</th>
                <th style={{ ...th, width: "27%" }}>Barcode</th>
                <th style={{ ...th, width: "24%" }}>Operation</th>
                <th style={{ ...th, width: 96 }}>Qty</th>
                <th style={{ ...th, width: 84, textAlign: "right" }}>Rate / Pc</th>
                <th style={{ ...th, width: 96, textAlign: "right" }}>Amount</th>
                <th style={{ ...th, width: 34 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const locked = !!r.billedBy;
                const so = r.soItem;
                const ops = opsFor(r);
                const op = opOf(r);
                const q = Number(r.qty) || 0;
                const conf = so ? opsBySo[so.saleOrderItemId] : null;
                const left = op && op.remainingQty != null && so
                  ? op.remainingQty - ((sheetQty[`${so.saleOrderItemId}|${r.operationId}`] || 0) - q)
                  : null;
                const over = !allowExtra && left != null && q > left;
                const dup = r.employeeId && so && r.operationId
                  && dupKeys.has(`${r.employeeId}|${so.saleOrderItemId}|${r.operationId}`);
                return (
                  <tr key={r.key} style={{ borderTop: `1px solid ${T.border}`, background: locked ? "var(--bg-hover)" : "var(--bg-card)" }}>
                    <td style={{ ...td, color: "var(--text-2)", fontSize: 12 }}>{i + 1}</td>
                    <td style={td}>
                      <Select2 value={r.employeeId} onChange={v => patchRow(r.key, { employeeId: v || "" })}
                        options={empOptions} placeholder="Employee…" disabled={locked} />
                    </td>
                    <td style={td}>
                      <BarcodePicker value={so} disabled={locked} locationId={locationId}
                        onPick={o => {
                          patchRow(r.key, { soItem: o, operationId: "", savedOp: null });
                          loadOps(o.saleOrderItemId);
                        }} />
                      {so && conf && !conf.configured && (
                        <div style={{ fontSize: 11.5, color: "var(--danger)", marginTop: 3 }}>
                          No operations linked - set them in Barcode Operations
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      <Select2 value={r.operationId} disabled={locked || !so}
                        onChange={v => patchRow(r.key, { operationId: v || "" })}
                        options={ops.map(o => ({
                          value: String(o.operationId),
                          label: `${o.operationName} · ${fmtAmt(o.jobWorkRate)}`
                            + (o.remainingQty != null ? ` · ${o.remainingQty} left` : "")
                            + (o.saved ? " · no longer linked" : ""),
                        }))}
                        placeholder={so ? "Operation…" : "Pick barcode first"} />
                      {dup && <div style={{ fontSize: 11.5, color: "var(--danger)", marginTop: 3 }}>Same employee, barcode and operation on another row</div>}
                    </td>
                    <td style={td}>
                      <input type="number" min="1" step="1" value={r.qty} disabled={locked}
                        onChange={e => patchRow(r.key, { qty: e.target.value })}
                        onKeyDown={e => { if (e.key === "Enter" && i === rows.length - 1) { e.preventDefault(); addRow(); } }}
                        style={{
                          width: 80, padding: "7px 8px", borderRadius: 6, fontSize: 13, fontWeight: 700,
                          outline: "none", color: "var(--text-0)", background: "var(--bg-card)",
                          border: `1px solid ${over ? "var(--danger)" : "var(--border)"}`,
                        }} />
                      {left != null && (
                        <div style={{ fontSize: 11, color: over ? "var(--danger)" : "var(--text-2)", marginTop: 2 }}>
                          {Math.max(0, left)} left
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>{op ? fmtAmt(op.jobWorkRate) : "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>
                      {op && q > 0 ? fmtAmt(q * (Number(op.jobWorkRate) || 0)) : "—"}
                      {locked && <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--success)" }}>Billed {r.billedBy}</div>}
                    </td>
                    <td style={td}>
                      {!locked && (
                        <button onClick={() => removeRow(r.key)} title="Remove row"
                          style={{ border: "none", background: "none", cursor: "pointer", color: "var(--danger)", display: "flex" }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: "8px 10px", borderTop: `1px solid ${T.border}` }}>
            <Btn size="sm" variant="secondary" icon={<Plus size={13} />} onClick={addRow}>Add row</Btn>
            <span style={{ marginLeft: 10, fontSize: 12, color: "var(--text-2)" }}>
              A new row starts with the previous row's employee and barcode · Enter in the last Qty adds a row
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ViewModal({ entryId, onClose }) {
  const [e, setE] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    stitchingEntryApi.getById(entryId)
      .then(res => setE(res.data?.data ?? res.data))
      .catch(ex => setErr(ex?.response?.data?.message || "Could not load the entry"));
  }, [entryId]);

  return (
    <Modal open onClose={onClose} width={900}
      title={e ? e.entryNumber : "Stitching Entry"}
      subtitle={e ? `${fmtDate(e.entryDate)} · ${(e.employeeNames && e.employeeNames.length ? e.employeeNames : [e.employeeName]).filter(Boolean).join(", ")}` : ""}
      footer={<Btn variant="secondary" icon={<X size={13} />} onClick={onClose}>Close</Btn>}>
      <Alert message={err} />
      {e && (
        <>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <Badge label={e.status} />
            <span style={{ fontSize: 13, color: "var(--text-1)" }}>
              Job Work: {(e.jobWorkEntryNumbers || []).length
                ? <b style={{ fontFamily: "monospace", color: "var(--success)" }}>{e.jobWorkEntryNumbers.join(", ")}</b>
                : <b style={{ color: "var(--warn)" }}>{e.status === "CONFIRMED" ? "pending" : "—"}</b>}
            </span>
            {e.createdByName && <span style={{ fontSize: 12, color: "var(--text-2)" }}>by {e.createdByName}</span>}
          </div>
          {e.notes && <Alert type="info" message={e.notes} />}
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--bg-input)" }}>
                  <th style={th}>Employee</th>
                  <th style={th}>Barcode</th>
                  <th style={th}>Product</th>
                  <th style={th}>Sale Order</th>
                  <th style={th}>Operation</th>
                  <th style={{ ...th, textAlign: "right" }}>Qty</th>
                  <th style={{ ...th, textAlign: "right" }}>Job Work Rate / Pc</th>
                  <th style={{ ...th, textAlign: "right" }}>Amount</th>
                  <th style={th}>Job Work</th>
                </tr>
              </thead>
              <tbody>
                {(e.items || []).map(it => (
                  <tr key={it.id} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={{ ...td, fontWeight: 600 }}>{it.employeeName || e.employeeName || "—"}</td>
                    <td style={{ ...td, fontFamily: "monospace", fontWeight: 700, color: "var(--primary)" }}>{it.barcode}</td>
                    <td style={td}>{it.productName}</td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>{it.soNumber}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{it.operationName}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{it.qty}</td>
                    <td style={{ ...td, textAlign: "right" }}>{fmtAmt(it.jobWorkRate)}</td>
                    <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmtAmt(it.amount)}</td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>
                      {it.jobWorkEntryNumber
                        ? <span style={{ color: "var(--success)", fontWeight: 700 }}>{it.jobWorkEntryNumber}</span>
                        : e.status === "CONFIRMED" ? <span style={{ color: "var(--warn)" }}>pending</span> : "—"}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: `2px solid ${T.border}`, background: "var(--bg-input)" }}>
                  <td style={{ ...td, fontWeight: 800 }} colSpan={5}>Total</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 800 }}>{e.totalQty}</td>
                  <td style={td} />
                  <td style={{ ...td, textAlign: "right", fontWeight: 800, color: "var(--success)" }}>{fmtAmt(e.totalAmount)}</td>
                  <td style={td} />
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

// CSS variables, so both themes read correctly.
const th = {
  textAlign: "left", padding: "7px 12px", fontSize: 11.5, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-2)",
  borderBottom: "1px solid var(--border)",
};
const td = { padding: "7px 12px", color: "var(--text-0)", verticalAlign: "middle" };
const dateInput = {
  padding: "7px 10px", borderRadius: 7, fontSize: 13, outline: "none",
  border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-0)",
};
